import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { User, Upload, Camera, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { toTitleCase } from "@/lib/utils";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

const formSchema = z.object({
  first_name: z.string().min(2, "El Nombre Debe Tener Al Menos 2 Caracteres"),
  last_name: z.string().min(2, "Los Apellidos Deben Tener Al Menos 2 Caracteres"),
  cedula: z.string().min(5, "La Cédula Debe Tener Al Menos 5 Caracteres"),
  birth_date: z.string().min(1, "La Fecha De Nacimiento Es Requerida"),
  birth_country: z.string().min(2, "El País De Nacimiento Es Requerido"),
  birth_city: z.string().min(2, "La Región / Provincia De Nacimiento Es Requerida"),
  birth_municipality: z.string().min(2, "La Ciudad De Nacimiento Es Requerida"),
  gender: z.string().min(1, "La Identidad De Género Es Requerida"),
  email: z.string().email("El Correo Electrónico No Es Válido"),
  phone: z.string().min(7, "El Teléfono Celular Es Requerido"),
  residence_country: z.string().min(2, "El País De Residencia Es Requerido"),
  residence_city: z.string().min(2, "La Región / Provincia De Residencia Es Requerida"),
  residence_municipality: z.string().min(2, "La Ciudad De Residencia Es Requerida"),
  nationality: z.string().min(2, "La Nacionalidad Es Requerida"),
});

const nationalities = [
  "Colombiana",
  "Mexicana",
  "Argentina",
  "Chilena",
  "Peruana",
  "Ecuatoriana",
  "Venezolana",
  "Brasileña",
  "Uruguaya",
  "Paraguaya",
  "Boliviana",
  "Panameña",
  "Costarricense",
  "Cubana",
  "Dominicana",
  "Estadounidense",
  "Española",
  "Italiana",
  "Holandés",
  "Otra",
];
const genderOptions = ["Femenino", "Masculino", "Binario", "No binario", "No deseo responder"];
const BIRTH_YEAR_MIN = 1920;
const birthMonthOptions = [
  { value: "01", label: "Enero" },
  { value: "02", label: "Febrero" },
  { value: "03", label: "Marzo" },
  { value: "04", label: "Abril" },
  { value: "05", label: "Mayo" },
  { value: "06", label: "Junio" },
  { value: "07", label: "Julio" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Septiembre" },
  { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" },
  { value: "12", label: "Diciembre" },
];

const parseBirthDateParts = (value) => {
  if (!value || typeof value !== "string") {
    return { year: "", month: "", day: "" };
  }
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return { year: "", month: "", day: "" };
  }
  return { year: match[1], month: match[2], day: match[3] };
};

const getDaysInMonth = (year, month) => {
  if (!year || !month) return 31;
  return new Date(Number(year), Number(month), 0).getDate();
};

const countryDisplayNames =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["es"], { type: "region" })
    : null;

const getSpanishCountryName = (country) => {
  const iso2 = String(country?.iso2 || "").toUpperCase();
  const originalName = String(country?.name || "").trim();
  if (!iso2 || !countryDisplayNames) {
    return originalName;
  }

  const localizedName = countryDisplayNames.of(iso2);
  if (!localizedName || typeof localizedName !== "string") {
    return originalName;
  }

  return localizedName.trim() || originalName;
};

const countryMatchesValue = (country, value, normalizeFn) => {
  const target = normalizeFn(value);
  if (!target) return false;
  return [country?.name, country?.original_name].some(
    (candidate) => normalizeFn(candidate) === target
  );
};

export default function PersonalDataForm({ profile, profileId, createProfile, updateProfile, onNext, loading }) {
  const normalizeGenderValue = (value) => {
    const normalized = value?.trim().toLocaleLowerCase("es") || "";
    if (normalized === "hombre") return "Masculino";
    if (normalized === "mujer") return "Femenino";
    if (normalized === "no binario" || normalized === "no-binario") return "No binario";
    if (normalized === "binario") return "Binario";
    return value || "";
  };
  const initialGender = normalizeGenderValue(profile?.personal_data?.gender || "");
  const initialBirthDateParts = parseBirthDateParts(profile?.personal_data?.birth_date);

  const [photoUrl, setPhotoUrl] = useState(profile?.personal_data?.photo_url || null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [selectedBirthYear, setSelectedBirthYear] = useState(initialBirthDateParts.year);
  const [selectedBirthMonth, setSelectedBirthMonth] = useState(initialBirthDateParts.month);
  const [selectedBirthDay, setSelectedBirthDay] = useState(initialBirthDateParts.day);
  const [countries, setCountries] = useState([]);
  const [countryCache, setCountryCache] = useState({});
  const [residenceStates, setResidenceStates] = useState([]);
  const [residenceCities, setResidenceCities] = useState([]);
  const [birthStates, setBirthStates] = useState([]);
  const [birthCities, setBirthCities] = useState([]);
  const [selectedCountryIso, setSelectedCountryIso] = useState("");
  const [selectedStateName, setSelectedStateName] = useState("");
  const [selectedCityName, setSelectedCityName] = useState("");
  const [selectedBirthCountryIso, setSelectedBirthCountryIso] = useState("");
  const [selectedBirthStateName, setSelectedBirthStateName] = useState("");
  const [selectedBirthCityName, setSelectedBirthCityName] = useState("");
  const [customResidenceMunicipality, setCustomResidenceMunicipality] = useState(
    profile?.personal_data?.residence_municipality || ""
  );
  const [customBirthMunicipality, setCustomBirthMunicipality] = useState(
    profile?.personal_data?.birth_municipality || ""
  );
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingBirthLocations, setLoadingBirthLocations] = useState(false);
  const [loadingResidenceLocations, setLoadingResidenceLocations] = useState(false);
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: profile?.personal_data?.first_name || "",
      last_name: profile?.personal_data?.last_name || "",
      cedula: profile?.personal_data?.cedula || "",
      birth_date: profile?.personal_data?.birth_date || "",
      birth_country: profile?.personal_data?.birth_country || "",
      birth_city: profile?.personal_data?.birth_city || "",
      birth_municipality: profile?.personal_data?.birth_municipality || "",
      gender: initialGender,
      email: profile?.personal_data?.email || "",
      phone: profile?.personal_data?.phone || "",
      residence_country: profile?.personal_data?.residence_country || "",
      residence_city: profile?.personal_data?.residence_city || "",
      residence_municipality: profile?.personal_data?.residence_municipality || "",
      nationality: profile?.personal_data?.nationality || "",
    },
  });

  const normalize = (value) => (value ? value.trim().toLocaleLowerCase("es") : "");

  const parseJson = async (response) => {
    const text = await response.text();
    const cleaned = text.replace(/^\uFEFF/, "");
    return JSON.parse(cleaned);
  };

  const sortStates = (states = []) =>
    [...states].sort((a, b) => a.name.localeCompare(b.name, "es"));
  const sortCities = (cities = []) =>
    [...cities].sort((a, b) => a.localeCompare(b, "es"));
  const currentYear = new Date().getFullYear();
  const birthYearOptions = Array.from(
    { length: currentYear - BIRTH_YEAR_MIN + 1 },
    (_, index) => String(currentYear - index)
  );
  const birthDayOptions =
    selectedBirthYear && selectedBirthMonth
      ? Array.from(
          { length: getDaysInMonth(selectedBirthYear, selectedBirthMonth) },
          (_, index) => String(index + 1).padStart(2, "0")
        )
      : [];

  const loadCountryData = async (iso3) => {
    if (!iso3) return null;
    if (countryCache[iso3]) {
      return countryCache[iso3];
    }
    const response = await fetch(`/countries/${iso3}.json`);
    const data = await parseJson(response);
    setCountryCache((prev) => ({ ...prev, [iso3]: data }));
    return data;
  };

  useEffect(() => {
    let isMounted = true;

    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const countriesResponse = await fetch("/countries/index.json");
        const countriesData = await parseJson(countriesResponse);
        if (!isMounted) return;
        const countriesList = (countriesData.countries || [])
          .map((country) => ({
            ...country,
            original_name: country.name,
            name: getSpanishCountryName(country),
          }))
          .sort((a, b) => a.name.localeCompare(b.name, "es"));
        setCountries(countriesList);
      } catch (error) {
        console.error("Error loading location data:", error);
      } finally {
        if (isMounted) {
          setLoadingCountries(false);
        }
      }
    };

    loadCountries();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!profile?.personal_data || countries.length === 0) {
      return;
    }
    if (selectedCountryIso) {
      return;
    }

    const countryMatch = countries.find((country) =>
      countryMatchesValue(country, profile.personal_data.residence_country, normalize)
    );
    if (!countryMatch) {
      return;
    }

    const countryIso = countryMatch.iso3;
    setSelectedCountryIso(countryIso);
    setValue("residence_country", countryMatch.name || "", { shouldValidate: false });

    setLoadingResidenceLocations(true);
    loadCountryData(countryIso)
      .then((data) => {
        const states = sortStates(data?.states || []);
        setResidenceStates(states);
        const stateMatch = states.find(
          (state) => normalize(state.name) === normalize(profile.personal_data.residence_city)
        );
        if (!stateMatch) {
          return;
        }
        setSelectedStateName(stateMatch.name);
        setValue("residence_city", stateMatch.name || "", { shouldValidate: false });
        const cities = sortCities(stateMatch.cities || []);
        setResidenceCities(cities);
        const cityMatch = cities.find(
          (city) => normalize(city) === normalize(profile.personal_data.residence_municipality)
        );
        if (cityMatch) {
          setSelectedCityName(cityMatch);
          setValue("residence_municipality", cityMatch || "", { shouldValidate: false });
        } else if (profile.personal_data.residence_municipality) {
          setSelectedCityName("otro");
          setCustomResidenceMunicipality(profile.personal_data.residence_municipality);
          setValue("residence_municipality", profile.personal_data.residence_municipality, {
            shouldValidate: false,
          });
        }
      })
      .catch((error) => {
        console.error("Error loading residence data:", error);
      })
      .finally(() => {
        setLoadingResidenceLocations(false);
      });
  }, [countries, profile, selectedCountryIso, setValue]);

  useEffect(() => {
    if (!profile?.personal_data || countries.length === 0) {
      return;
    }
    if (selectedBirthCountryIso) {
      return;
    }

    const countryMatch = countries.find((country) =>
      countryMatchesValue(country, profile.personal_data.birth_country, normalize)
    );
    if (!countryMatch) {
      return;
    }

    const countryIso = countryMatch.iso3;
    setSelectedBirthCountryIso(countryIso);
    setValue("birth_country", countryMatch.name || "", { shouldValidate: false });

    setLoadingBirthLocations(true);
    loadCountryData(countryIso)
      .then((data) => {
        const states = sortStates(data?.states || []);
        setBirthStates(states);
        const stateMatch = states.find(
          (state) => normalize(state.name) === normalize(profile.personal_data.birth_city)
        );
        if (!stateMatch) {
          return;
        }
        setSelectedBirthStateName(stateMatch.name);
        setValue("birth_city", stateMatch.name || "", { shouldValidate: false });
        const cities = sortCities(stateMatch.cities || []);
        setBirthCities(cities);
        const cityMatch = cities.find(
          (city) => normalize(city) === normalize(profile.personal_data.birth_municipality)
        );
        if (cityMatch) {
          setSelectedBirthCityName(cityMatch);
          setValue("birth_municipality", cityMatch || "", { shouldValidate: false });
        } else if (profile.personal_data.birth_municipality) {
          setSelectedBirthCityName("otro");
          setCustomBirthMunicipality(profile.personal_data.birth_municipality);
          setValue("birth_municipality", profile.personal_data.birth_municipality, {
            shouldValidate: false,
          });
        }
      })
      .catch((error) => {
        console.error("Error loading birth data:", error);
      })
      .finally(() => {
        setLoadingBirthLocations(false);
      });
  }, [countries, profile, selectedBirthCountryIso, setValue]);

  const handleBirthCountryChange = async (value) => {
    setSelectedBirthCountryIso(value);
    const country = countries.find((item) => item.iso3 === value);
    setValue("birth_country", country?.name || "", { shouldValidate: true });
    setSelectedBirthStateName("");
    setSelectedBirthCityName("");
    setCustomBirthMunicipality("");
    setBirthStates([]);
    setBirthCities([]);
    setValue("birth_city", "", { shouldValidate: true });
    setValue("birth_municipality", "", { shouldValidate: true });

    if (!value) return;
    setLoadingBirthLocations(true);
    try {
      const data = await loadCountryData(value);
      const states = sortStates(data?.states || []);
      setBirthStates(states);
    } catch (error) {
      console.error("Error loading birth data:", error);
    } finally {
      setLoadingBirthLocations(false);
    }
  };

  const handleBirthStateChange = (value) => {
    setSelectedBirthStateName(value);
    setValue("birth_city", value || "", { shouldValidate: true });
    setSelectedBirthCityName("");
    setCustomBirthMunicipality("");
    setBirthCities([]);
    setValue("birth_municipality", "", { shouldValidate: true });
    const state = birthStates.find((item) => item.name === value);
    const cities = sortCities(state?.cities || []);
    setBirthCities(cities);
  };

  const handleBirthCityChange = (value) => {
    setSelectedBirthCityName(value);
    if (value === "otro") {
      setCustomBirthMunicipality("");
      setValue("birth_municipality", "", { shouldValidate: true });
      return;
    }
    setCustomBirthMunicipality("");
    setValue("birth_municipality", value || "", { shouldValidate: true });
  };

  const handleCountryChange = async (value) => {
    setSelectedCountryIso(value);
    const country = countries.find((item) => item.iso3 === value);
    setValue("residence_country", country?.name || "", { shouldValidate: true });
    setSelectedStateName("");
    setSelectedCityName("");
    setCustomResidenceMunicipality("");
    setResidenceStates([]);
    setResidenceCities([]);
    setValue("residence_city", "", { shouldValidate: true });
    setValue("residence_municipality", "", { shouldValidate: true });

    if (!value) return;
    setLoadingResidenceLocations(true);
    try {
      const data = await loadCountryData(value);
      const states = sortStates(data?.states || []);
      setResidenceStates(states);
    } catch (error) {
      console.error("Error loading residence data:", error);
    } finally {
      setLoadingResidenceLocations(false);
    }
  };

  const handleStateChange = (value) => {
    setSelectedStateName(value);
    setValue("residence_city", value || "", { shouldValidate: true });
    setSelectedCityName("");
    setCustomResidenceMunicipality("");
    setResidenceCities([]);
    setValue("residence_municipality", "", { shouldValidate: true });
    const state = residenceStates.find((item) => item.name === value);
    const cities = sortCities(state?.cities || []);
    setResidenceCities(cities);
  };

  const handleCityChange = (value) => {
    setSelectedCityName(value);
    if (value === "otro") {
      setCustomResidenceMunicipality("");
      setValue("residence_municipality", "", { shouldValidate: true });
      return;
    }
    setCustomResidenceMunicipality("");
    setValue("residence_municipality", value || "", { shouldValidate: true });
  };

  const handleCustomBirthMunicipalityChange = (event) => {
    const value = event.target.value;
    setCustomBirthMunicipality(value);
    setValue("birth_municipality", value, { shouldValidate: true });
  };

  const handleCustomBirthMunicipalityBlur = (event) => {
    const value = toTitleCase(event.target.value);
    setCustomBirthMunicipality(value);
    setValue("birth_municipality", value, { shouldValidate: true });
  };

  const handleCustomResidenceMunicipalityChange = (event) => {
    const value = event.target.value;
    setCustomResidenceMunicipality(value);
    setValue("residence_municipality", value, { shouldValidate: true });
  };

  const handleCustomResidenceMunicipalityBlur = (event) => {
    const value = toTitleCase(event.target.value);
    setCustomResidenceMunicipality(value);
    setValue("residence_municipality", value, { shouldValidate: true });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Por Favor Selecciona Una Imagen Válida");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("La Imagen No Debe Superar Los 5MB");
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(`${BACKEND_URL}${response.data.url}`);
      setPhotoError("");
      toast.success("Foto Subida Correctamente");
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Error Al Subir La Foto");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const syncBirthDateValue = (year, month, day) => {
    if (year && month && day) {
      setValue("birth_date", `${year}-${month}-${day}`, { shouldValidate: true });
      return;
    }
    setValue("birth_date", "", { shouldValidate: true });
  };

  const handleBirthYearChange = (value) => {
    const maxDay = getDaysInMonth(value, selectedBirthMonth);
    const nextDay =
      selectedBirthDay && Number(selectedBirthDay) > maxDay ? "" : selectedBirthDay;
    setSelectedBirthYear(value);
    setSelectedBirthDay(nextDay);
    syncBirthDateValue(value, selectedBirthMonth, nextDay);
  };

  const handleBirthMonthChange = (value) => {
    const maxDay = getDaysInMonth(selectedBirthYear, value);
    const nextDay =
      selectedBirthDay && Number(selectedBirthDay) > maxDay ? "" : selectedBirthDay;
    setSelectedBirthMonth(value);
    setSelectedBirthDay(nextDay);
    syncBirthDateValue(selectedBirthYear, value, nextDay);
  };

  const handleBirthDayChange = (value) => {
    setSelectedBirthDay(value);
    syncBirthDateValue(selectedBirthYear, selectedBirthMonth, value);
  };

  const onSubmit = async (data) => {
    if (!photoUrl) {
      setPhotoError("La Foto De Perfil Es Obligatoria");
      toast.error("La Foto De Perfil Es Obligatoria");
      return;
    }

    let currentProfileId = profileId;
    
    if (!currentProfileId) {
      currentProfileId = await createProfile();
      if (!currentProfileId) return;
    }

    const personalData = {
      ...data,
      photo_url: photoUrl,
    };

    const result = await updateProfile({ personal_data: personalData });
    if (result) {
      toast.success("Datos Personales Guardados");
      onNext();
    }
  };

  return (
    <div data-testid="personal-data-form">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-yellow-400 rounded flex items-center justify-center">
            <User className="w-5 h-5 text-black" />
          </div>
          <h2 className="font-oswald text-2xl lg:text-3xl font-bold text-black tracking-wide">
            Datos Personales
          </h2>
        </div>
        <p className="text-gray-500 ml-13">Complete Su Información Personal Básica</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Photo Upload */}
        <div className="flex flex-col items-center mb-8">
          <div
            className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200 cursor-pointer hover:border-yellow-400 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            data-testid="photo-upload-area"
          >
            {photoUrl ? (
              <img
                src={photoUrl}
                alt="Foto De Perfil"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <Camera className="w-10 h-10 text-gray-400" />
              </div>
            )}
            {uploadingPhoto && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            className="hidden"
            data-testid="photo-input"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-2 text-gray-600"
            onClick={() => {
              setPhotoError("");
              fileInputRef.current?.click();
            }}
            disabled={uploadingPhoto}
          >
            <Upload className="w-4 h-4 mr-2" />
            {photoUrl ? "Cambiar Foto" : "Subir Foto"}
          </Button>
          {photoError && (
            <p className="text-red-500 text-sm mt-2">{photoError}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name */}
          <div className="form-field">
            <Label htmlFor="first_name" className="text-sm font-medium text-gray-700 tracking-wide">
              Nombres
            </Label>
            <Input
              id="first_name"
              {...register("first_name", {
                onBlur: (event) => {
                  const value = toTitleCase(event.target.value);
                  event.target.value = value;
                  setValue("first_name", value, { shouldValidate: true });
                },
              })}
              placeholder="Ingrese Sus Nombres"
              className="mt-1 h-12 rounded-sm border-gray-300 focus:border-yellow-400 focus:ring-yellow-400"
              data-testid="first-name-input"
            />
            {errors.first_name && (
              <p className="text-red-500 text-sm mt-1">{errors.first_name.message}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="form-field">
            <Label htmlFor="last_name" className="text-sm font-medium text-gray-700 tracking-wide">
              Apellidos
            </Label>
            <Input
              id="last_name"
              {...register("last_name", {
                onBlur: (event) => {
                  const value = toTitleCase(event.target.value);
                  event.target.value = value;
                  setValue("last_name", value, { shouldValidate: true });
                },
              })}
              placeholder="Ingrese Sus Apellidos"
              className="mt-1 h-12 rounded-sm border-gray-300 focus:border-yellow-400 focus:ring-yellow-400"
              data-testid="last-name-input"
            />
            {errors.last_name && (
              <p className="text-red-500 text-sm mt-1">{errors.last_name.message}</p>
            )}
          </div>

          {/* Cedula */}
          <div className="form-field">
            <Label htmlFor="cedula" className="text-sm font-medium text-gray-700 tracking-wide">
              Cédula / Documento De Identidad
            </Label>
            <Input
              id="cedula"
              {...register("cedula")}
              placeholder="Ingrese Su Número De Cédula"
              className="mt-1 h-12 rounded-sm border-gray-300 focus:border-yellow-400 focus:ring-yellow-400"
              data-testid="cedula-input"
            />
            {errors.cedula && (
              <p className="text-red-500 text-sm mt-1">{errors.cedula.message}</p>
            )}
          </div>

          {/* Birth Date */}
          <div className="form-field">
            <Label className="text-sm font-medium text-gray-700 tracking-wide">
              Fecha De Nacimiento
            </Label>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select value={selectedBirthYear || undefined} onValueChange={handleBirthYearChange}>
                <SelectTrigger className="h-12 rounded-sm border-gray-300" data-testid="birth-date-year-select">
                  <SelectValue placeholder="Año" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {birthYearOptions.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={selectedBirthMonth || undefined}
                onValueChange={handleBirthMonthChange}
                disabled={!selectedBirthYear}
              >
                <SelectTrigger className="h-12 rounded-sm border-gray-300" data-testid="birth-date-month-select">
                  <SelectValue placeholder="Mes" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {birthMonthOptions.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={selectedBirthDay || undefined}
                onValueChange={handleBirthDayChange}
                disabled={!selectedBirthYear || !selectedBirthMonth}
              >
                <SelectTrigger className="h-12 rounded-sm border-gray-300" data-testid="birth-date-day-select">
                  <SelectValue placeholder="Día" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {birthDayOptions.map((day) => (
                    <SelectItem key={day} value={day}>
                      {day}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <input type="hidden" {...register("birth_date")} />
            {errors.birth_date && (
              <p className="text-red-500 text-sm mt-1">{errors.birth_date.message}</p>
            )}
          </div>

          
          {/* Email */}
          <div className="form-field">
            <Label htmlFor="email" className="text-sm font-medium text-gray-700 tracking-wide">
              Correo Electrónico
            </Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              placeholder="Ingrese Su Correo Electrónico"
              className="mt-1 h-12 rounded-sm border-gray-300 focus:border-yellow-400 focus:ring-yellow-400"
              data-testid="email-input"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div className="form-field">
            <Label htmlFor="phone" className="text-sm font-medium text-gray-700 tracking-wide">
              Teléfono Celular De Contacto
            </Label>
            <Input
              id="phone"
              type="tel"
              {...register("phone")}
              placeholder="Ingrese Su Teléfono Celular"
              className="mt-1 h-12 rounded-sm border-gray-300 focus:border-yellow-400 focus:ring-yellow-400"
              data-testid="phone-input"
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
            )}
          </div>

          {/* Gender */}
          <div className="form-field">
            <Label className="text-sm font-medium text-gray-700 tracking-wide">
              Identidad De Género
            </Label>
            <Select
              defaultValue={initialGender}
              onValueChange={(value) => setValue("gender", value, { shouldValidate: true })}
            >
              <SelectTrigger 
                className="mt-1 h-12 rounded-sm border-gray-300"
                data-testid="gender-select"
              >
                <SelectValue placeholder="Seleccione Su Identidad De Género" />
              </SelectTrigger>
              <SelectContent>
                {genderOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" {...register("gender")} />
            {errors.gender && (
              <p className="text-red-500 text-sm mt-1">{errors.gender.message}</p>
            )}
          </div>

          <div className="form-field md:col-span-2 pt-2 border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-500 tracking-wide">
              Lugar De Nacimiento
            </p>
          </div>

          {/* Birth Country */}
          <div className="form-field">
            <Label className="text-sm font-medium text-gray-700 tracking-wide">
              País De Nacimiento
            </Label>
            <Select
              value={selectedBirthCountryIso || undefined}
              onValueChange={handleBirthCountryChange}
              disabled={loadingCountries}
            >
              <SelectTrigger 
                className="mt-1 h-12 rounded-sm border-gray-300"
                data-testid="birth-country-select"
              >
                <SelectValue
                  placeholder={loadingCountries ? "Cargando Países..." : "Seleccione Un País"}
                />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.iso3} value={country.iso3}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" {...register("birth_country")} />
            {errors.birth_country && (
              <p className="text-red-500 text-sm mt-1">{errors.birth_country.message}</p>
            )}
          </div>

          {/* Birth Region/Province */}
          <div className="form-field">
            <Label className="text-sm font-medium text-gray-700 tracking-wide">
              Región / Provincia De Nacimiento
            </Label>
            <Select
              value={selectedBirthStateName || undefined}
              onValueChange={handleBirthStateChange}
              disabled={loadingCountries || loadingBirthLocations || !selectedBirthCountryIso}
            >
              <SelectTrigger 
                className="mt-1 h-12 rounded-sm border-gray-300"
                data-testid="birth-city-select"
              >
                <SelectValue
                  placeholder={
                    loadingCountries || loadingBirthLocations
                      ? "Cargando Regiones / Provincias..."
                      : selectedBirthCountryIso
                      ? "Seleccione Una Región / Provincia"
                      : "Seleccione Un País Primero"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {birthStates.map((state, index) => (
                  <SelectItem key={`${state.name}-${index}`} value={state.name}>
                    {state.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" {...register("birth_city")} />
            {errors.birth_city && (
              <p className="text-red-500 text-sm mt-1">{errors.birth_city.message}</p>
            )}
          </div>

          {/* Birth City */}
          <div className="form-field md:col-span-2">
            <Label className="text-sm font-medium text-gray-700 tracking-wide">
              Ciudad De Nacimiento
            </Label>
            <Select
              value={selectedBirthCityName || undefined}
              onValueChange={handleBirthCityChange}
              disabled={loadingCountries || loadingBirthLocations || !selectedBirthStateName}
            >
              <SelectTrigger 
                className="mt-1 h-12 rounded-sm border-gray-300"
                data-testid="birth-municipality-select"
              >
                <SelectValue
                  placeholder={
                    loadingCountries || loadingBirthLocations
                      ? "Cargando Ciudades..."
                      : selectedBirthStateName
                      ? "Seleccione Una Ciudad"
                      : "Seleccione Una Región / Provincia Primero"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {birthCities.map((city, index) => (
                  <SelectItem key={`${city}-${index}`} value={city}>
                    {city}
                  </SelectItem>
                ))}
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" {...register("birth_municipality")} />
            {errors.birth_municipality && (
              <p className="text-red-500 text-sm mt-1">{errors.birth_municipality.message}</p>
            )}
            {selectedBirthCityName === "otro" && (
              <Input
                value={customBirthMunicipality}
                onChange={handleCustomBirthMunicipalityChange}
                onBlur={handleCustomBirthMunicipalityBlur}
                placeholder="Escriba Su Ciudad"
                className="mt-3 h-12 rounded-sm"
                data-testid="birth-municipality-other-input"
              />
            )}
          </div>

          <div className="form-field md:col-span-2 pt-2 border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-500 tracking-wide">
              Lugar De Residencia
            </p>
          </div>

          {/* Residence Country */}
          <div className="form-field">
            <Label className="text-sm font-medium text-gray-700 tracking-wide">
              País De Residencia
            </Label>
            <Select
              value={selectedCountryIso || undefined}
              onValueChange={handleCountryChange}
              disabled={loadingCountries}
            >
              <SelectTrigger 
                className="mt-1 h-12 rounded-sm border-gray-300"
                data-testid="residence-country-select"
              >
                <SelectValue
                  placeholder={loadingCountries ? "Cargando Países..." : "Seleccione Un País"}
                />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.iso3} value={country.iso3}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" {...register("residence_country")} />
            {errors.residence_country && (
              <p className="text-red-500 text-sm mt-1">{errors.residence_country.message}</p>
            )}
          </div>

          {/* Residence Region/Province */}
          <div className="form-field">
            <Label className="text-sm font-medium text-gray-700 tracking-wide">
              Región / Provincia De Residencia
            </Label>
            <Select
              value={selectedStateName || undefined}
              onValueChange={handleStateChange}
              disabled={loadingCountries || loadingResidenceLocations || !selectedCountryIso}
            >
              <SelectTrigger 
                className="mt-1 h-12 rounded-sm border-gray-300"
                data-testid="residence-city-select"
              >
                <SelectValue
                  placeholder={
                    loadingCountries || loadingResidenceLocations
                      ? "Cargando Regiones / Provincias..."
                      : selectedCountryIso
                      ? "Seleccione Una Región / Provincia"
                      : "Seleccione Un País Primero"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {residenceStates.map((state, index) => (
                  <SelectItem key={`${state.name}-${index}`} value={state.name}>
                    {state.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" {...register("residence_city")} />
            {errors.residence_city && (
              <p className="text-red-500 text-sm mt-1">{errors.residence_city.message}</p>
            )}
          </div>

          {/* Residence City */}
          <div className="form-field md:col-span-2">
            <Label className="text-sm font-medium text-gray-700 tracking-wide">
              Ciudad De Residencia
            </Label>
            <Select
              value={selectedCityName || undefined}
              onValueChange={handleCityChange}
              disabled={loadingCountries || loadingResidenceLocations || !selectedStateName}
            >
              <SelectTrigger 
                className="mt-1 h-12 rounded-sm border-gray-300"
                data-testid="residence-municipality-select"
              >
                <SelectValue
                  placeholder={
                    loadingCountries || loadingResidenceLocations
                      ? "Cargando Ciudades..."
                      : selectedStateName
                      ? "Seleccione Una Ciudad"
                      : "Seleccione Una Región / Provincia Primero"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {residenceCities.map((city, index) => (
                  <SelectItem key={`${city}-${index}`} value={city}>
                    {city}
                  </SelectItem>
                ))}
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" {...register("residence_municipality")} />
            {errors.residence_municipality && (
              <p className="text-red-500 text-sm mt-1">{errors.residence_municipality.message}</p>
            )}
            {selectedCityName === "otro" && (
              <Input
                value={customResidenceMunicipality}
                onChange={handleCustomResidenceMunicipalityChange}
                onBlur={handleCustomResidenceMunicipalityBlur}
                placeholder="Escriba Su Ciudad"
                className="mt-3 h-12 rounded-sm"
                data-testid="residence-municipality-other-input"
              />
            )}
          </div>

          {/* Nationality */}
          <div className="form-field">
            <Label className="text-sm font-medium text-gray-700 tracking-wide">
              Nacionalidad
            </Label>
            <Select
              defaultValue={profile?.personal_data?.nationality || ""}
              onValueChange={(value) => setValue("nationality", value)}
            >
              <SelectTrigger 
                className="mt-1 h-12 rounded-sm border-gray-300"
                data-testid="nationality-select"
              >
                <SelectValue placeholder="Seleccione Su Nacionalidad" />
              </SelectTrigger>
              <SelectContent>
                {nationalities.map((nat) => (
                  <SelectItem key={nat} value={nat}>
                    {nat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" {...register("nationality")} />
            {errors.nationality && (
              <p className="text-red-500 text-sm mt-1">{errors.nationality.message}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t">
          <Button
            type="submit"
            disabled={loading}
            className="bg-black text-yellow-400 hover:bg-gray-900 hover:text-yellow-300 tracking-wide font-bold px-8 h-12 rounded-sm btn-primary"
            data-testid="personal-data-submit"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Siguiente
                <ChevronRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
